
import { useState, useEffect } from "react";
import { Canvas, Image as FabricImage } from "fabric";
import { useAuth } from "@/components/AuthProvider";
import { CustomCanvas } from "@/types/fabric";
import { ScenarioType, ScenarioObject, AnalysisState } from "@/types/analysis";
import { loadingMessages } from "@/constants/analysis";
import { 
  saveAnalysis, 
  loadExistingAnalysis, 
  getAnalysisPrompt, 
  analyzeDrawingWithAI 
} from "@/services/analysis";

export const useAnalysis = (canvas: Canvas | null, currentDrawingId: string | null) => {
  const [state, setState] = useState<AnalysisState>({
    isAnalyzing: false,
    loadingMessage: "",
    activeScenario: null,
    analysisResult: null,
    optimisticResult: null,
    error: null
  });
  
  const { user } = useAuth();

  const resetError = () => {
    setState(prev => ({
      ...prev,
      error: null,
      optimisticResult: null
    }));
  };

  const resetAnalysis = () => {
    console.log('Resetting analysis state in use-analysis.ts');
    
    // Clear the state
    setState({
      isAnalyzing: false,
      loadingMessage: "",
      activeScenario: null,
      analysisResult: null,
      optimisticResult: null,
      error: null
    });

    // Also clear analysis data from the canvas if available
    if (canvas && (canvas as CustomCanvas).data) {
      console.log('Clearing analysis data from canvas');
      (canvas as CustomCanvas).data = {
        ...(canvas as CustomCanvas).data,
        analysisText: null,
        scenarioType: null,
        procedureType: null
      };
    }
  };

  // Detect when scenarios are added or removed
  useEffect(() => {
    if (!canvas) return;

    const detectActiveScenario = () => {
      console.log('Detecting active scenario...');
      
      // STEP 1: First check if we have a scenario type stored in canvas data
      // This is important for drawings loaded from history where the scenario info is saved
      const canvasData = (canvas as CustomCanvas).data;
      if (canvasData?.scenarioType) {
        console.log('Found scenario type in canvas data:', canvasData.scenarioType);
        setState(prev => ({ ...prev, activeScenario: canvasData.scenarioType as ScenarioType }));
        return;
      }
      
      // STEP 2: If no canvas data, then check canvas objects
      const scenarios = canvas.getObjects().filter((obj) => {
        if (!(obj instanceof FabricImage)) return false;
        
        const scenarioObj = obj as ScenarioObject;
        console.log('Checking object for scenario_type:', scenarioObj.scenario_type);
        return scenarioObj.scenario_type;
      });

      // If we have scenario objects, use the first one's type
      if (scenarios.length > 0) {
        const scenarioObj = scenarios[0] as ScenarioObject;
        console.log('Found active scenario from objects:', scenarioObj.scenario_type);
        
        // Update both state and canvas data
        setState(prev => ({ ...prev, activeScenario: scenarioObj.scenario_type || null }));
        
        // Also update canvas data for future reference
        if (scenarioObj.scenario_type && canvas) {
          (canvas as CustomCanvas).data = {
            ...(canvas as CustomCanvas).data,
            scenarioType: scenarioObj.scenario_type
          };
        }
      } else {
        console.log('No active scenario found');
        // Reset analysis when last scenario is removed
        if (state.activeScenario !== null) {
          console.log('Last scenario removed, resetting analysis');
          resetAnalysis();
        } else {
          setState(prev => ({ ...prev, activeScenario: null }));
        }
      }
    };

    detectActiveScenario();
    
    canvas.on('object:added', detectActiveScenario);
    canvas.on('object:removed', detectActiveScenario);

    return () => {
      canvas.off('object:added', detectActiveScenario);
      canvas.off('object:removed', detectActiveScenario);
    };
  }, [canvas, state.activeScenario]);

  // Check for analysis text in the canvas data when it changes
  useEffect(() => {
    if (!canvas) return;
    
    const canvasData = (canvas as CustomCanvas).data;
    // Add more detailed logging to debug the issue
    console.log('Checking for analysis data in canvas:', canvasData);
    
    if (canvasData?.analysisText && !state.analysisResult) {
      console.log('Found analysis text in canvas data, updating UI state:', canvasData.analysisText);
      setState(prev => ({
        ...prev,
        analysisResult: canvasData.analysisText,
        activeScenario: canvasData.scenarioType as ScenarioType || prev.activeScenario
      }));
    }
  }, [canvas, state.analysisResult, currentDrawingId]); // Add currentDrawingId dependency

  // Reset analysis when currentDrawingId changes to null or canvas becomes null
  useEffect(() => {
    if (!currentDrawingId || !canvas) {
      console.log('Drawing ID or canvas is null, resetting analysis result only');
      setState(prev => ({ ...prev, analysisResult: null }));
      return;
    }

    console.log('Current drawing ID changed to:', currentDrawingId);
    
    // Check if we already have analysis data in the canvas
    const canvasData = (canvas as CustomCanvas).data;
    if (canvasData?.analysisText) {
      console.log('Analysis data already exists in canvas:', canvasData.analysisText);
      setState(prev => ({
        ...prev,
        analysisResult: canvasData.analysisText,
        activeScenario: canvasData.scenarioType as ScenarioType || prev.activeScenario
      }));
      return;
    }

    try {
      // We don't need this anymore since we're loading the analysis with the drawing
      // Only keep this for legacy drawings that don't have analysis embedded
      const loadAnalysis = async () => {
        // Only load if there's no analysis already
        if (!(canvas as CustomCanvas).data?.analysisText) {
          const analysisData = await loadExistingAnalysis(currentDrawingId, canvas);
          if (analysisData) {
            setState(prev => ({
              ...prev,
              analysisResult: analysisData.analysis_text,
              activeScenario: analysisData.procedure_type as ScenarioType
            }));
          }
        }
      };

      loadAnalysis();
    } catch (error) {
      console.error('Failed to load existing analysis:', error);
      // Don't set error state since loading existing analysis is optional
    }
  }, [currentDrawingId, canvas]);

  const analyzeDrawing = async (scenarioType: ScenarioType) => {
    if (!canvas) {
      throw new Error('Canvas is not available');
    }
    
    setState(prev => ({ 
      ...prev, 
      isAnalyzing: true, 
      error: null 
    }));
    
    try {
      const promptData = await getAnalysisPrompt(scenarioType);
      
      // Get canvas data URL with both scenario image and drawing visible
      const dataUrl = canvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: 1,
      });

      const data = await analyzeDrawingWithAI(dataUrl, scenarioType, promptData.version);
      
      // Update canvas data with analysis result
      (canvas as CustomCanvas).data = {
        ...(canvas as CustomCanvas).data,
        scenarioType,
        analysisText: data.analysis
      };

      // Attempt to save analysis if we have a drawing ID and user
      if (currentDrawingId && user) {
        await saveAnalysis(
          data.analysis, 
          scenarioType, 
          currentDrawingId, 
          user.id,
          canvas
        );
      }
      
      setState(prev => ({
        ...prev,
        analysisResult: data.analysis,
        optimisticResult: null
      }));
      
    } catch (error: any) {
      console.error('Error analyzing drawing:', error);
      setState(prev => ({
        ...prev,
        error: error.message || "There was an error analyzing your drawing. Please try again."
      }));
      throw error;
    } finally {
      setState(prev => ({ ...prev, isAnalyzing: false }));
    }
  };

  useEffect(() => {
    if (state.isAnalyzing) {
      let currentIndex = 0;
      const interval = setInterval(() => {
        setState(prev => ({ 
          ...prev, 
          loadingMessage: loadingMessages[currentIndex] 
        }));
        currentIndex = (currentIndex + 1) % loadingMessages.length;
      }, 2000);

      return () => {
        clearInterval(interval);
        setState(prev => ({ ...prev, loadingMessage: "" }));
      };
    }
  }, [state.isAnalyzing]);

  return {
    isAnalyzing: state.isAnalyzing,
    loadingMessage: state.loadingMessage,
    analysisResult: state.analysisResult,
    activeScenario: state.activeScenario,
    analyzeDrawing,
    error: state.error,
    resetError,
    resetAnalysis,
    optimisticResult: state.optimisticResult,
    setOptimisticResult: (optimisticResult: string | null) => 
      setState(prev => ({ ...prev, optimisticResult }))
  };
};
